/**
 * Hurtigknappene på Livelogg.
 *
 * ========  PER BRUKER PER PRODUKSJON, OG BARE LOKALT  ========
 *
 * Den ufravikelige regelen sier at hurtigknapper er per bruker per
 * produksjon — produksjonsstyrte knapper kommer i fase 5. I appen ligger de
 * i den lokale basen og lastes aldri opp: `loggsynk` sender ikke engang
 * `button_id` med posten, fordi en fremmednøkkel til en rad som ikke finnes
 * ville avvist hele posten.
 *
 * Her er valget det samme, og av samme grunn. Knappene er ERGONOMI, ikke
 * innhold: etiketten som betyr noe er frosset på posten i trykkøyeblikket,
 * og den står der uansett hva knappen senere heter eller om den slettes.
 * Da er det ingenting å synkronisere.
 *
 * Prisen er at knappene hører til nettleseren, ikke til personen. Den som
 * logger fra en annen maskin setter dem opp på nytt. Det er samme pris
 * appen betaler, og den er verdt å betale til auth finnes — da vet vi hvem
 * personen ER, og kan flytte dem med.
 *
 * ========  FAST PALETT  ========
 *
 * Fargene er appens, lest fra samme liste. Ingen fri fargevelger: da er
 * kontrasten prøvd i dårlig lys, og oppsettet går raskere. En fri velger
 * gir uvegerlig en knapp i mørkeblått som ikke synes.
 */

/** Appens palett. Verdiene er rene heks, så de betyr det samme begge steder. */
export const MARKOERFARGER = {
  gra: "#6E6A63",
  gul: "#C0A33E",
  bla: "#4E7FA8",
  rust: "#8E5C4A",
  lilla: "#8A5FA0",
  gronn: "#5E8F5A",
} as const;

/** Rekkefølgen nye knapper får farge i. Som i appen. */
export const PALETT: readonly string[] = [
  MARKOERFARGER.gul,
  MARKOERFARGER.bla,
  MARKOERFARGER.lilla,
  MARKOERFARGER.rust,
  MARKOERFARGER.gronn,
  MARKOERFARGER.gra,
];

export interface Hurtigknapp {
  readonly id: string;
  readonly etikett: string;
  readonly farge: string;
}

function noekkel(produksjonId: string, medlemId: string): string {
  return `infoflyt.hurtigknapper.${produksjonId}.${medlemId}`;
}

export function les(produksjonId: string, medlemId: string): Hurtigknapp[] {
  try {
    const r = localStorage.getItem(noekkel(produksjonId, medlemId));
    if (r === null) return [];
    const liste = JSON.parse(r) as unknown;
    if (!Array.isArray(liste)) return [];
    return liste.flatMap((x) => {
      if (typeof x !== "object" || x === null) return [];
      const o = x as Record<string, unknown>;
      if (typeof o["id"] !== "string" || typeof o["etikett"] !== "string") return [];
      return [{
        id: o["id"],
        etikett: o["etikett"],
        farge: typeof o["farge"] === "string" ? o["farge"] : MARKOERFARGER.gra,
      }];
    });
  } catch {
    /* Privat modus kan nekte lagring. Da er lista tom, og flata virker —
       man logger bare uten hurtigknapper. */
    return [];
  }
}

export function skriv(
  produksjonId: string, medlemId: string, knapper: readonly Hurtigknapp[],
): void {
  try {
    localStorage.setItem(noekkel(produksjonId, medlemId), JSON.stringify(knapper));
  } catch {
    /* Ingenting å gjøre. Knappene gjelder denne økta. */
  }
}

/** Neste farge i rekka, slik at to naboknapper sjelden blir like. */
export function nesteFarge(antall: number): string {
  return PALETT[antall % PALETT.length] ?? MARKOERFARGER.gra;
}
